import { z } from 'zod';
import { ID_PREFIXES, type IdKind } from './id';

/** Zod schema for a prefixed id of one kind. */
export function id<K extends IdKind>(kind: K) {
  const prefix = ID_PREFIXES[kind];
  return z
    .string()
    .regex(new RegExp(`^${prefix}_[0-9a-f]{32}$`), `expected a ${kind} id like ${prefix}_<32 hex>`);
}

/**
 * ISO 8601 in UTC, always with a Z suffix and no offset. Offsets are rejected so that string
 * comparison on the wire is the same as chronological comparison, and so that parquet and Athena
 * never have to guess a timezone.
 */
export const isoTimestamp = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z$/, 'expected an ISO 8601 UTC timestamp')
  .refine((v) => {
    // Date.parse happily rolls 2026-02-30 forward into March, so matching the regex is not enough.
    // Re-serialising and comparing is what actually rejects a date that does not exist.
    const parsed = new Date(v);
    if (Number.isNaN(parsed.getTime())) return false;
    const withMillis = v.length === 20 ? v.replace('Z', '.000Z') : v;
    return parsed.toISOString() === withMillis;
  }, 'not a real date');

/**
 * Consent captured at the moment the event happened.
 *
 * `storage_allowed` is stored rather than derived, so that nothing downstream has to re-implement
 * TCF purpose logic to know whether a device id was legitimately persisted.
 */
export const consentSchema = z
  .object({
    status: z.enum(['granted', 'denied', 'unknown']),
    source: z.enum(['tcf', 'host', 'none']),
    storage_allowed: z.boolean(),
    /** IAB TCF purpose ids the reader consented to. */
    purposes: z.array(z.number().int().min(1).max(11)).max(11).optional(),
    /** Raw TC string. Contains no personal data, only vendor and purpose bits. */
    tcf_string: z.string().max(2048).optional(),
  })
  .strict()
  .refine((c) => !c.storage_allowed || c.status === 'granted', {
    message: 'storage_allowed requires consent status granted',
    path: ['storage_allowed'],
  })
  .refine((c) => c.source !== 'tcf' || c.tcf_string !== undefined, {
    message: 'consent sourced from tcf must carry the tc string',
    path: ['tcf_string'],
  });

export type Consent = z.infer<typeof consentSchema>;

/**
 * Progressive identity.
 *
 * `session_id` is always present and lives in memory only. `device_id` is persisted in first party
 * storage on the publisher's own domain and therefore only exists with storage consent.
 * `identity_ref` points at a record in the PII vault. No schema in this package ever carries an
 * email, a name or any other direct identifier.
 */
export const identitySchema = z
  .object({
    session_id: id('session'),
    device_id: id('device').optional(),
    identity_ref: id('identity').optional(),
  })
  .strict();

export type Identity = z.infer<typeof identitySchema>;

/** Where on the publisher's site the interaction was shown. */
export const contextSchema = z
  .object({
    /** Sanitised with `sanitisePageUrl` before it reaches this schema. */
    page_url: z.string().max(2048),
    page_type: z.enum(['article', 'section', 'homepage', 'other']),
    /** The publisher's own content id, when the embed supplies one. */
    article_id: z.string().max(200).optional(),
    /** Host only. The full referrer can carry search terms and other personal data. */
    referrer_host: z.string().max(255).optional(),
    locale: z.string().max(35).optional(),
    /**
     * Which placement this embed is, when a publisher names one with `data-quizee-ref`.
     *
     * The same interaction can run in an article, on a homepage and in a newsletter, and a
     * publisher wants those apart. Author supplied, not reader supplied, so it is a slug rather
     * than free text and it must never carry personal data: it ends up on every event, and the
     * whole point of the other rules here is that a reader's own words do not.
     */
    placement_ref: z
      .string()
      .max(64)
      .regex(/^[A-Za-z0-9][A-Za-z0-9_.:/-]*$/, 'a placement reference is a short slug')
      .optional(),
    viewport_width: z.number().int().min(0).max(20000).optional(),
  })
  .strict();

export type PageContext = z.infer<typeof contextSchema>;

/** Server observed facts. Added at ingest, never accepted from the client. */
export const ingestSchema = z
  .object({
    /** Salted SHA-256 of the client IP. The raw address is never stored. */
    ip_hash: z.string().regex(/^[0-9a-f]{64}$/).optional(),
    country: z.string().regex(/^[A-Z]{2}$/).optional(),
    device_type: z.enum(['mobile', 'tablet', 'desktop', 'bot', 'unknown']),
  })
  .strict();

export type IngestMeta = z.infer<typeof ingestSchema>;

/**
 * Strip a page url down to something safe to store.
 *
 * Query strings on publisher pages routinely carry email addresses, campaign identifiers tied to a
 * newsletter recipient, and session tokens. Everything is dropped except an explicit allowlist,
 * and the fragment always goes.
 */
export function sanitisePageUrl(input: string, allowedParams: readonly string[] = []): string {
  let url: URL;
  try {
    url = new URL(input);
  } catch {
    throw new Error('page url must be absolute');
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    throw new Error('page url must be http or https');
  }

  const kept = new URLSearchParams();
  for (const name of allowedParams) {
    const value = url.searchParams.get(name);
    if (value !== null) kept.set(name, value);
  }

  url.search = kept.toString();
  url.hash = '';
  url.username = '';
  url.password = '';
  return url.toString();
}
